import React,{useState, useEffect, useMemo} from 'react';
import AddCoundelor from '../counselor/addCounselor';
import StaffApplicationReadOnly, {resolveSnapshotSections} from '../counselor/staffApplication/StaffApplicationReadOnly';
import FormVersionChoiceModal from '../counselor/staffApplication/FormVersionChoiceModal';
import {counselordetail} from '../../services/directorServices';
import {fetchStaffFormSchema} from '../../services/staffFormService';
import { connect } from 'react-redux';
import settings from '../../config/settings';
import {ProgramLoader} from '../reuse/SVG';

/*
 * A counselor's own staff application, at /profile/information.
 *
 * View-first. This page used to drop straight into the editable form with a "Submit application"
 * button and no way to simply look at what had been submitted — which also meant it rendered the
 * camp's CURRENT questions, so anything the director had since deleted was invisible to the person
 * who answered it. The read-only view renders their own snapshot instead, so their answers are
 * always visible even when the live form no longer asks those questions.
 *
 * Editing asks which version to work from, but only when there is a real choice to make: the form
 * has moved on since they submitted. Otherwise Edit opens the form directly, as before.
 */
const CampPersonnelProfileEdit =(props)=>{

    const[counselorDetails, setCounselorDetails] = useState({});
    const[loading, setLoading]                   = useState(true);
    const[editMode, setEditMode]                 = useState(false);
    // The camp's form as it stands now — fetched only for its version number, which is what
    // decides whether there is a choice worth asking about at all. The form itself is loaded
    // again by the edit form when it needs it.
    const[currentForm, setCurrentForm]           = useState(null);
    const[versionModal, setVersionModal]         = useState(false);
    /*
     * Which form the open edit is against: { sections, formVersion }. Null means "whatever the
     * form loads for itself", which is the camp's current version — the path a director takes and
     * the one a counselor already on the current version still takes.
     */
    const[editTarget, setEditTarget]             = useState(null);

    const load = () => {
        let counselorId = props.userProfile._id;
        return counselordetail(`/?counselorId=${counselorId}`).then((res) => {
            if(res.data.statusCode === 1){
                setCounselorDetails(res.data.responseData.result);
            }
            setLoading(false);
        });
    };

    useEffect(() => { load(); },[])

    // Failure is not fatal: currentForm stays null, no version choice is offered, and Edit behaves
    // exactly as it did before this screen learned about versions.
    useEffect(() => {
        const campCode = props.userProfile?.campCode;
        if (!campCode) return;
        fetchStaffFormSchema(campCode)
            .then(setCurrentForm)
            .catch(() => {});
    },[props.userProfile])

    // Re-read on the way out of edit mode so the view reflects what was just saved, and so a new
    // answer to a question added since submission appears immediately rather than after a refresh.
    const leaveEditMode = () => {
        setLoading(true);
        setEditMode(false);
        setEditTarget(null);
        load();
    };

    // The version this record was submitted on. Only used to decide whether to offer a choice —
    // showing it to the person is StaffApplicationReadOnly's job, shared with the director's
    // personnel screen so the two cannot word it differently.
    const stamp = counselorDetails.staffFormVersion;

    /*
     * Both non-null, and actually different. A record that predates versioning has no stamp, and a
     * camp whose schema failed to load has no current version — in neither case is there an honest
     * question to put to the person, so Edit just opens the form.
     *
     * Gated on the flag as well, because only addCounselorNew reads the override props. Stamping
     * is deliberately never gated, so with the flag off a record still carries a version that may
     * differ from current — and the modal would then offer a choice the legacy form silently
     * ignores, which is worse than not asking.
     */
    const currentVersion = currentForm?.currentVersion;
    const versionChanged = settings.USE_NEW_STAFF_FORM
        && stamp != null && currentVersion != null && stamp !== currentVersion;

    const snapshotSections = useMemo(() => resolveSnapshotSections(counselorDetails), [counselorDetails]);

    const startEdit = () => {
        if (versionChanged) { setVersionModal(true); return; }
        setEditTarget(null);
        setEditMode(true);
    };

    const chooseVersion = target => {
        setVersionModal(false);
        setEditTarget(target);
        setEditMode(true);
    };

    return(
        <div className="card mb-4 pro_wrapper">
        {loading ? <div className="page_loader"><ProgramLoader /></div> : ''}
        <h4 className="text-center mb-5">User Information</h4>

        <div className="row campPersonell_profile">
            {editMode ? (
                /*
                 * EditfromPersonellProfile is still passed: addCounselorNew ignores it, but the
                 * legacy form reads it to suppress its own "Edit Personnel Information" heading,
                 * which would otherwise sit under the "User Information" one above.
                 *
                 * The override props are undefined unless a version was chosen, which is what
                 * keeps the unchanged-version path byte-identical to before.
                 */
                <AddCoundelor
                    Details={counselorDetails}
                    EditfromPersonellProfile={true}
                    sectionsOverride={editTarget?.sections}
                    formVersionOverride={editTarget?.formVersion}
                    HideEditmode={leaveEditMode}
                    onSaved={leaveEditMode} />
            ) : (
                <div className="col-lg-12">
                    <StaffApplicationReadOnly details={counselorDetails} />
                    {/* Same treatment as the director's record view, so the one action on both
                        screens looks like the same action. */}
                    {!loading ? (
                        <div className="text-end mb-4">
                            <button
                                onClick={startEdit}
                                className="btn btn-primary"
                                style={{ minWidth: '150px' }}>Edit application</button>
                        </div>
                    ) : null}
                </div>
            )}
        </div>

        {versionModal ? (
            <FormVersionChoiceModal
                submittedVersion={stamp}
                currentVersion={currentVersion}
                onKeepOriginal={() => chooseVersion({ sections: snapshotSections, formVersion: stamp })}
                // No override: the plain edit already renders the camp's current form merged with
                // this record's snapshot, which is exactly what "show the updated form" means.
                // Building the same union a second time here would be two copies of one rule,
                // free to drift apart.
                onShowUpdated={() => chooseVersion(null)}
                onClose={() => setVersionModal(false)} />
        ) : null}
        </div>
    )

}


const mapStateToProps = state => {
    let { userProfile, loggedIn } = state.user
    return {
        userProfile,
        loggedIn
    };
}

export default connect(mapStateToProps, null)(CampPersonnelProfileEdit);
